/**
 * Shared scanner sound system.
 *
 * 3 beeps padronizados pra feedback de bipe nos coletores:
 *   - error   → 2 tons descendentes (graves) — "BEEP-BOOP"
 *   - warning → 1 tom médio sustentado
 *   - success → 2 tons ascendentes (sine, suaves) — "BIP-BIP"
 *
 * Reutiliza UM único AudioContext global pra evitar criar/destruir context
 * a cada bipe e pra contornar o gesture-lock dos navegadores. O context
 * só destrava após uma interação do usuário (keydown/click/touch), portanto
 * o hook useAudioUnlock() deve ser chamado em telas que tocam som.
 */

import { useEffect } from 'react'

let _ctx = null

function getCtx() {
  if (typeof window === 'undefined') return null
  if (_ctx) return _ctx
  const AC = window.AudioContext || window.webkitAudioContext
  if (!AC) return null
  _ctx = new AC()
  return _ctx
}

function ensureUnlocked() {
  const ctx = getCtx()
  if (!ctx) return null
  if (ctx.state === 'suspended') ctx.resume().catch(() => {})
  return ctx
}

/**
 * Hook que liga listeners globais no document pra destravar o AudioContext
 * no primeiro gesture do usuário. Idempotente — chamar em quantas telas
 * quiser, sempre destrava o mesmo singleton.
 */
export function useAudioUnlock() {
  useEffect(() => {
    if (typeof window === 'undefined') return
    const unlock = () => ensureUnlocked()
    window.addEventListener('click', unlock)
    window.addEventListener('keydown', unlock)
    window.addEventListener('touchstart', unlock)
    return () => {
      window.removeEventListener('click', unlock)
      window.removeEventListener('keydown', unlock)
      window.removeEventListener('touchstart', unlock)
    }
  }, [])
}

function _playTones(tones) {
  try {
    const ctx = ensureUnlocked()
    if (!ctx) return
    const now = ctx.currentTime
    for (const t of tones) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain); gain.connect(ctx.destination)
      osc.type = t.type || 'square'
      const start = now + (t.offset || 0)
      const dur = t.dur || 0.14
      osc.frequency.setValueAtTime(t.freq, start)
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(t.vol || 0.35, start + 0.012)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + dur)
      osc.start(start); osc.stop(start + dur + 0.02)
    }
  } catch (e) {
    console.warn('[sounds] play failed:', e)
  }
}

/** Som de erro — 2 tons descendentes (280 → 200 Hz, square) */
export function playErrorBeep() {
  _playTones([
    { freq: 280, dur: 0.14, type: 'square', vol: 0.4 },
    { freq: 200, dur: 0.14, type: 'square', vol: 0.4, offset: 0.16 },
  ])
}

/** Som de warning — 1 tom médio sustentado (440 Hz, square) */
export function playWarningBeep() {
  _playTones([
    { freq: 440, dur: 0.22, type: 'square', vol: 0.35 },
  ])
}

/** Som de sucesso — 2 tons ascendentes em sine (660 → 880 Hz) */
export function playSuccessBeep() {
  _playTones([
    { freq: 660, dur: 0.10, type: 'sine', vol: 0.28 },
    { freq: 880, dur: 0.14, type: 'sine', vol: 0.28, offset: 0.10 },
  ])
}

/** Helper: dado um cenário, escolhe o beep certo */
export function playBeepFor(kind) {
  if (kind === 'success' || kind === 'ok') return playSuccessBeep()
  if (kind === 'warning' || kind === 'warn') return playWarningBeep()
  return playErrorBeep()
}
